import { useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Sparkle, Megaphone } from "@phosphor-icons/react";

/**
 * One-time batch-send surface for the Follow-feature announcement.
 *
 * The endpoint is idempotent — every owner gets the email at most once
 * (dedup via `users.follow_announce_sent_at`). Re-clicking only mails
 * brand-new owners who haven't received it yet, so the button is safe
 * to leave around indefinitely as a "fill in the gaps" tool whenever a
 * new owner is invited before the next batch.
 */
export default function FollowAnnouncementCard() {
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (
      !window.confirm(
        "Send the one-time \"Follow Truck\" feature announcement to every existing owner who hasn't received it yet? Each owner is mailed at most once.",
      )
    ) {
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/announcements/follow-feature");
      toast.success(r.data?.message || "Announcement batch started.");
    } catch (e) {
      toast.error(formatApiError(e, "Announcement send failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mb-6" data-testid="follow-announcement-card">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone size={18} weight="duotone" className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight" data-testid="follow-announcement-heading">
            One-time announcement: Follow Truck feature
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          Sends "<span className="text-foreground">Your customers can now follow your truck 👀</span>"
          to every existing owner with a real email. Idempotent — each owner only gets it once,
          so re-clicking after onboarding new owners is safe and topical.
        </p>
        <div className="flex items-center gap-3">
          <Button
            onClick={send}
            disabled={sending}
            data-testid="follow-announcement-send"
            className="flex items-center gap-2"
          >
            <Sparkle size={16} weight="fill" />
            {sending ? "Starting…" : "Send announcement"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Watch the Email Log below for delivery confirmations.
          </span>
        </div>
      </div>
    </section>
  );
}
